import React, { useState, useEffect } from 'react';
import { useConfig } from '../contexts/ConfigContext';
import { validateSMMLV, validateExchangeRate } from '../utils/validators';
import { getCustomRates, saveCustomRates, clearCustomRates, getSalaryTypeLabel, CustomRates, SalaryTypeKey } from '../services/customRatesService';
import { Save, RotateCcw, ChevronDown, Trash2, FolderPlus, Settings, TrendingUp } from 'lucide-react';
import { DEFAULT_RATES_BY_TYPE } from '../utils/constants';
import styles from './ConfigView.module.css';

// Tasas por defecto según tipo de salario (basado en ctcCalculator.ts)
// Tasas por defecto centralizadas en utils/constants.ts
// const DEFAULT_RATES_BY_TYPE... (removed)


interface ConceptDef {
  name: string;
  label: string;
  isFixed?: boolean;
  isFixedUSD?: boolean;
}

// Secciones con todos los conceptos importadas de constants
import { CONCEPT_SECTIONS } from '../utils/constants';

// Conceptos que NO son editables (solo salario base)
const NON_EDITABLE_CONCEPTS = ['Salary', 'G&A Colombia'];
// Connectivity assistance SÍ es editable

export function ConfigView() {
  const { config, isLoading, setSMMLV, setExchangeRate, setGAPercentage, setHighSalaryMultiplier, setIntegralMultiplier, setOperationalCosts, resetConfig } = useConfig();
  const [saved, setSaved] = useState(false);
  const [isSalaryTypeOpen, setIsSalaryTypeOpen] = useState(false);
  const [isThresholdsExpanded, setIsThresholdsExpanded] = useState(false);

  const [selectedSalaryType, setSelectedSalaryType] = useState<SalaryTypeKey>('ordinario_inf');
  const [editedRates, setEditedRates] = useState<CustomRates>({});
  const [rateInputDrafts, setRateInputDrafts] = useState<Record<string, string>>({});

  // Estado local para costos operativos
  const [operationalCosts, setOperationalCostsLocal] = useState(config.operationalCosts);

  const smmlvValidation = validateSMMLV(config.smmlv);
  const exchangeValidation = validateExchangeRate(config.exchangeRate);

  const handleSalaryTypeChange = (type: SalaryTypeKey) => {
    setSelectedSalaryType(type);
    const rates = getCustomRates(type);
    setEditedRates({ ...rates });
    setRateInputDrafts({});
  };

  useEffect(() => {
    const rates = getCustomRates(selectedSalaryType);
    setEditedRates({ ...rates });
    setRateInputDrafts({});
  }, []);

  useEffect(() => {
    setOperationalCostsLocal(config.operationalCosts);
  }, [config.operationalCosts]);

  // Loading check AFTER all hooks
  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p>Cargando configuración...</p>
      </div>
    );
  }

  const normalizeInput = (value: string): string => value.replace(',', '.').trim();

  const parseRateInput = (value: string, isFixed: boolean = false, isFixedUSD: boolean = false): number | null => {
    const normalized = normalizeInput(value);
    if (!normalized || normalized === '.' || normalized === '-')
      return null;

    const numValue = Number(normalized);
    if (!Number.isFinite(numValue) || numValue < 0)
      return null;

    return (isFixed || isFixedUSD) ? numValue : numValue / 100;
  };

  const handleRateChange = (conceptName: string, value: string, isFixed: boolean = false, isFixedUSD: boolean = false, fromCopToUsd: boolean = false) => {
    setRateInputDrafts(prev => ({
      ...prev,
      [conceptName]: value
    }));

    let parsed = parseRateInput(value, isFixed, isFixedUSD);
    if (parsed === null) return;

    if (fromCopToUsd) {
      parsed = parsed / config.exchangeRate;
    }

    setEditedRates(prev => ({
      ...prev,
      [conceptName]: parsed
    }));
  };

  const handleRateBlur = (conceptName: string, isFixed: boolean = false, isFixedUSD: boolean = false) => {
    const draft = rateInputDrafts[conceptName];
    if (draft !== undefined) {
      const parsed = parseRateInput(draft, isFixed, isFixedUSD);
      if (parsed !== null) {
        setEditedRates(prev => ({
          ...prev,
          [conceptName]: parsed
        }));
      }
    }

    setRateInputDrafts(prev => {
      if (prev[conceptName] === undefined) return prev;
      const next = { ...prev };
      delete next[conceptName];
      return next;
    });
  };

  const handleSaveAll = async () => {
    if (smmlvValidation.valid && exchangeValidation.valid) {
      // Guardar tasas personalizadas
      saveCustomRates(editedRates, selectedSalaryType);
      // Guardar costos operativos
      await setOperationalCosts(operationalCosts);
      setRateInputDrafts({});
      // Los parámetros legales ya se guardan automáticamente con cada cambio
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }
  };

  const handleResetAll = async () => {
    // Resetear parámetros legales y costos operativos
    await resetConfig();
    // Limpiar tasas personalizadas del tipo actual
    clearCustomRates(selectedSalaryType);
    setEditedRates({});
    setRateInputDrafts({});
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleOperationalCostChange = (field: keyof typeof operationalCosts, value: number) => {
    setOperationalCostsLocal(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getDefaultRate = (conceptName: string): number => {
    return (DEFAULT_RATES_BY_TYPE[selectedSalaryType] as Record<string, number>)[conceptName] ?? 0;
  };

  const getDisplayValue = (conceptName: string, isFixed: boolean = false, isFixedUSD: boolean = false): any => {
    const draft = rateInputDrafts[conceptName];
    if (draft !== undefined) {
      return draft;
    }

    const defaultRate = getDefaultRate(conceptName);
    const value = editedRates[conceptName] !== undefined ? editedRates[conceptName] : defaultRate;

    if (isFixedUSD) {
      return value.toFixed(2); // Retorna el valor en USD con decimales
    }
    if (isFixed) {
      return Math.round(value).toString(); // Retorna el valor en COP
    }
    return (value * 100).toFixed(2); // Retorna el porcentaje
  };

  const getEditableCopInputValue = (conceptName: string, isFixedUSD: boolean): string => {
    const draft = rateInputDrafts[conceptName];
    if (draft !== undefined) {
      return draft;
    }

    const defaultRate = getDefaultRate(conceptName);
    const value = editedRates[conceptName] !== undefined ? editedRates[conceptName] : defaultRate;
    if (isFixedUSD) {
      return Math.round(value * config.exchangeRate).toString();
    }
    return Math.round(value).toString();
  };

  const getDefaultDisplay = (conceptName: string, isFixed: boolean = false, isFixedUSD: boolean = false): string => {
    const defaultRate = getDefaultRate(conceptName);
    if (isFixedUSD) return `${defaultRate.toLocaleString()} USD`;
    if (isFixed) return `${defaultRate.toLocaleString()} COP`;
    return `${(defaultRate * 100).toFixed(2)}%`;
  };

  const getUnit = (isFixed: boolean = false, isFixedUSD: boolean = false): string => {
    if (isFixedUSD) return 'USD';
    if (isFixed) return 'COP';
    return '%';
  };

  const getCOPValue = (conceptName: string, isFixed: boolean = false, isFixedUSD: boolean = false): string => {
    const value = editedRates[conceptName] !== undefined ? editedRates[conceptName] : getDefaultRate(conceptName);
    if (isFixedUSD) return (value * config.exchangeRate).toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    if (isFixed) return value.toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    return '-';
  };

  const getUSDValue = (conceptName: string, isFixed: boolean = false, isFixedUSD: boolean = false): string => {
    const value = editedRates[conceptName] !== undefined ? editedRates[conceptName] : getDefaultRate(conceptName);
    if (isFixedUSD) return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (isFixed) return (value / config.exchangeRate).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return '-';
  };

  const hasCustomRate = (conceptName: string): boolean => {
    return editedRates[conceptName] !== undefined;
  };

  const isEditable = (conceptName: string): boolean => {
    return !NON_EDITABLE_CONCEPTS.includes(conceptName);
  };


  return (
    <div className={styles.container}>
      {/* Header Card with Parameters */}
      <div className={styles.headerCard}>
        <div className={styles.cardHeader}>
          <div className={styles.cardHeaderLeft}>
            <Settings size={18} className={styles.configIcon} />
            <h2 className={styles.configTitle}>Configuración del Sistema</h2>
          </div>
          {saved && (
            <div className={styles.successMessageInline}>
              ✓ Guardado correctamente
            </div>
          )}
          <div className={styles.actionButtons}>
            <button className={styles.saveBtn} onClick={handleSaveAll}>
              <Save size={16} />
              <span>Guardar</span>
            </button>
            <button className={styles.resetBtn} onClick={handleResetAll}>
              <RotateCcw size={16} />
              <span>Restaurar</span>
            </button>
          </div>
        </div>

        {/* Parameters Row Inside Card */}
        <div className={styles.parametersRow}>
          {/* Tipo de Salario */}
          <div className={styles.parameterGroup}>
            <label className={styles.parameterLabel}>Tipo de Salario</label>
            <div className={styles.selectWrapper}>
              <select
                value={selectedSalaryType}
                onChange={(e) => handleSalaryTypeChange(e.target.value as SalaryTypeKey)}
                onFocus={() => setIsSalaryTypeOpen(true)}
                onBlur={() => setIsSalaryTypeOpen(false)}
                className={styles.parameterSelect}
              >
                <option value="ordinario_inf">{getSalaryTypeLabel('ordinario_inf')}</option>
                <option value="ordinario_sup">{getSalaryTypeLabel('ordinario_sup')}</option>
                <option value="integral">{getSalaryTypeLabel('integral')}</option>
              </select>
              <ChevronDown className={`${styles.selectIcon} ${isSalaryTypeOpen ? styles.selectIconOpen : ''}`} size={16} />
            </div>
          </div>

          {/* SMMLV */}
          <div className={styles.parameterGroup}>
            <label className={styles.parameterLabel}>SMMLV</label>
            <input
              type="number"
              className={styles.parameterInput}
              value={config.smmlv}
              onChange={(e) => setSMMLV(Number(e.target.value))}
            />
            {smmlvValidation.errors[0] && (
              <span className={styles.parameterError}>{smmlvValidation.errors[0]}</span>
            )}
          </div>

          {/* Tasa de Cambio */}
          <div className={styles.parameterGroup}>
            <label className={styles.parameterLabel}>Tasa de Cambio</label>
            <input
              type="number"
              className={styles.parameterInput}
              value={config.exchangeRate}
              onChange={(e) => setExchangeRate(Number(e.target.value))}
            />
            {exchangeValidation.errors[0] && (
              <span className={styles.parameterError}>{exchangeValidation.errors[0]}</span>
            )}
          </div>

          {/* G&A */}
          <div className={styles.parameterGroup}>
            <label className={styles.parameterLabel}>G&A (%)</label>
            <input
              type="number"
              className={styles.parameterInput}
              value={config.gaPercentage}
              onChange={(e) => setGAPercentage(Number(e.target.value))}
            />
          </div>
        </div>
      </div>

      <div className={styles.mainLayout}>
        <div className={styles.leftColumn}>
          {/* Rates Table - Sin contenedor adicional */}
          <table className={styles.ratesTable}>
            <thead>
              <tr>
                <th>CONCEPTO</th>
                <th>REF / %</th>
                <th>COP (MENSUAL)</th>
                <th>USD (MENSUAL)</th>
              </tr>
            </thead>
            <tbody>
              {CONCEPT_SECTIONS.map((section) => (
                <React.Fragment key={`${section.title}-${selectedSalaryType}`}>
                  <tr className={styles.sectionHeader}>
                    <td colSpan={4}>{section.title}</td>
                  </tr>
                  {section.concepts.map((concept) => (
                    <tr key={`${concept.name}-${selectedSalaryType}`}>
                      <td className={styles.conceptCell}>{concept.label}</td>
                      <td className={styles.inputCell}>
                        {isEditable(concept.name) && !concept.isFixed && !concept.isFixedUSD ? (
                          <div className={styles.inputWrapper}>
                            <input
                              type="text"
                              inputMode="decimal"
                              className={`${styles.rateInput} ${hasCustomRate(concept.name) ? styles.hasCustomValue : ''}`}
                              value={getDisplayValue(concept.name, false, false)}
                              onChange={(e) => handleRateChange(concept.name, e.target.value, false, false)}
                              onBlur={() => handleRateBlur(concept.name, false, false)}
                            />
                            <span className={styles.rateUnit}>%</span>
                          </div>
                        ) : (
                          <span className={styles.nonEditable}>-</span>
                        )}
                      </td>
                      <td className={styles.inputCell}>
                        {isEditable(concept.name) && (concept.isFixed || concept.isFixedUSD) ? (
                          <div className={styles.inputWrapper}>
                            <span className={styles.currencyPrefix}>$</span>
                            <input
                              type="text"
                              inputMode="decimal"
                              className={`${styles.rateInput} ${hasCustomRate(concept.name) ? styles.hasCustomValue : ''}`}
                              value={getEditableCopInputValue(concept.name, !!concept.isFixedUSD)}
                              onChange={(e) => {
                                if (concept.isFixedUSD) {
                                  // El usuario edita en COP, convertimos a USD para el estado interno
                                  handleRateChange(concept.name, e.target.value, false, true, true);
                                } else {
                                  // Si es COP o porcentaje, guardar directamente
                                  handleRateChange(concept.name, e.target.value, !!concept.isFixed, !!concept.isFixedUSD);
                                }
                              }}
                              onBlur={() => handleRateBlur(concept.name, concept.isFixed, concept.isFixedUSD)}
                            />
                          </div>
                        ) : (
                          <span>$ {getCOPValue(concept.name, concept.isFixed, concept.isFixedUSD)}</span>
                        )}
                      </td>
                      <td className={styles.defaultCell}>$ {getUSDValue(concept.name, concept.isFixed, concept.isFixedUSD)}</td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        <div className={styles.rightColumn}>
          {/* Card 1: Factor Analysis (Premium Style) */}
          {/* Card 1: Factor Analysis Removed */}

          {/* Card 2: Thresholds (Standard Style) */}
          <div className={styles.paramsPanel} style={{ marginTop: '0' }}> {/* Removed top margin for alignment */}
            <div
              className={styles.paramsPanelHeader}
              onClick={() => setIsThresholdsExpanded(!isThresholdsExpanded)}
              style={{
                cursor: 'pointer',
                userSelect: 'none',
                borderBottom: isThresholdsExpanded ? '1px solid rgba(255,255,255,0.05)' : 'none',
                paddingBottom: isThresholdsExpanded ? '12px' : '0',
                marginBottom: isThresholdsExpanded ? '24px' : '0' // Dynamic margin
              }}
            >
              <Settings size={18} />
              <h3>Umbrales de Salario</h3>
              <ChevronDown
                size={18}
                style={{
                  transform: isThresholdsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                  marginLeft: 'auto'
                }}
              />
            </div>

            {isThresholdsExpanded && (
              <div style={{ marginTop: '12px' }}>
                <div className={styles.paramGroup}>
                  <label className={styles.paramLabel}>Ordinario Inferior</label>
                  <div className={styles.thresholdRange}>
                    <span className={styles.thresholdValue}>0</span>
                    <span className={styles.thresholdSeparator}>a</span>
                    <span className={styles.thresholdValue}>&lt; {((config.highSalaryMultiplier || 10) * config.smmlv).toLocaleString()} COP</span>
                  </div>
                  <span className={styles.paramHint}>&lt; {config.highSalaryMultiplier || 10} SMMLV</span>
                </div>

                <div className={styles.paramGroup}>
                  <label className={styles.paramLabel}>Ordinario Superior (× SMMLV)</label>
                  <input
                    type="number"
                    className={styles.paramInput}
                    value={config.highSalaryMultiplier || 10}
                    onChange={(e) => setHighSalaryMultiplier(Number(e.target.value))}
                  />
                  <span className={styles.paramHint}>≥ {((config.highSalaryMultiplier || 10) * config.smmlv).toLocaleString()} COP</span>
                </div>

                <div className={styles.paramGroup}>
                  <label className={styles.paramLabel}>Integral (× SMMLV)</label>
                  <input
                    type="number"
                    className={styles.paramInput}
                    value={config.integralMultiplier || 13}
                    onChange={(e) => setIntegralMultiplier(Number(e.target.value))}
                  />
                  <span className={styles.paramHint}>≥ {((config.integralMultiplier || 13) * config.smmlv).toLocaleString()} COP</span>
                </div>
              </div>
            )}

            {saved && (
              <div className={styles.paramSuccess}>✓ Guardado correctamente</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
